export const RESOURCE_POOL_TYPE = Object.freeze({
    ACCOUNT: 'account',
    WALLET: 'wallet'
})

export const RESOURCE_POOL_FILTER_TYPE = Object.freeze({
    ROOT_ACCOUNT: 'rootAccount',
    ACCOUNT: RESOURCE_POOL_TYPE.ACCOUNT,
    WALLET: RESOURCE_POOL_TYPE.WALLET
})

export const RESOURCE_POOL_ROLES = ['rootManager', 'manager', 'member']

export const RESOURCE_POOL_ROLE_OPTIONS = Object.freeze([
    { value: 'rootManager', label: 'Root manager', icon: 'mdi-crown-outline' },
    { value: 'manager', label: 'Manager', icon: 'mdi-office-building-outline' },
    { value: 'member', label: 'Member', icon: 'mdi-account-outline' }
])

export function resourcePoolRoleMetadata(role) {
    const normalizedRole = String(role || '')
        .trim()
        .toLowerCase()
        .replace(/[_\s]/g, '')
    const option = RESOURCE_POOL_ROLE_OPTIONS.find(option => option.value.toLowerCase() === normalizedRole)
    return option ? { key: option.value, label: option.label, icon: option.icon } : { key: 'unknown', label: 'Unavailable', icon: 'mdi-account-outline' }
}

export function resourcePoolRole(pool = {}, fallback = 'member') {
    if (pool.isRootManager === true || pool.is_root_manager === true) return 'rootManager'
    if (pool.is_rp_manager === true) return 'manager'
    return fallback
}

export function resourcePoolRoleLabel(pool, fallback) {
    const role = resourcePoolRole(pool, fallback)
    return RESOURCE_POOL_ROLE_OPTIONS.find(option => option.value === role)?.label || ''
}

export const RESOURCE_POOL_API_TYPE = Object.freeze({
    ACCOUNT: 'ACCOUNT',
    WALLET: 'WALLET'
})

export const RESOURCE_POOL_TYPES = [RESOURCE_POOL_TYPE.ACCOUNT, RESOURCE_POOL_TYPE.WALLET]

export const RESOURCE_POOL_FILTER_TYPES = [RESOURCE_POOL_FILTER_TYPE.ROOT_ACCOUNT, RESOURCE_POOL_FILTER_TYPE.ACCOUNT, RESOURCE_POOL_FILTER_TYPE.WALLET]

export const RESOURCE_POOL_TYPE_OPTIONS = Object.freeze([
    { value: RESOURCE_POOL_TYPE.ACCOUNT, label: 'Accounts', icon: 'mdi-account-box-outline' },
    { value: RESOURCE_POOL_TYPE.WALLET, label: 'Wallets', icon: 'mdi-wallet-outline' }
])

export const RESOURCE_POOL_FILTER_TYPE_OPTIONS = Object.freeze([
    { value: RESOURCE_POOL_FILTER_TYPE.ROOT_ACCOUNT, label: 'Root account', icon: 'mdi-briefcase-variant-outline' },
    { value: RESOURCE_POOL_FILTER_TYPE.ACCOUNT, label: 'Accounts', icon: 'mdi-account-box-outline' },
    { value: RESOURCE_POOL_FILTER_TYPE.WALLET, label: 'Wallets', icon: 'mdi-wallet-outline' }
])

export function normalizeResourcePoolType(resourcePoolType) {
    if (typeof resourcePoolType === 'number') {
        if (resourcePoolType === 0) return RESOURCE_POOL_TYPE.ACCOUNT
        if (resourcePoolType === 1) return RESOURCE_POOL_TYPE.WALLET
    }

    const value = String(resourcePoolType ?? '')
        .trim()
        .toLowerCase()
    if (value === 'account' || value === 'accounts' || value === '0') return RESOURCE_POOL_TYPE.ACCOUNT
    if (value === 'wallet' || value === 'wallets' || value === '1') return RESOURCE_POOL_TYPE.WALLET
    return null
}

export function isSameResourcePoolId(left, right) {
    if (left === null || left === undefined || right === null || right === undefined) return false
    return String(left) === String(right)
}

export function isRootResourcePool(pool = {}) {
    return normalizeResourcePoolType(pool.resource_pool_type) === RESOURCE_POOL_TYPE.ACCOUNT && isSameResourcePoolId(pool.rpid, pool.root_rpid)
}

export function resourcePoolFilterType(pool = {}) {
    if (isRootResourcePool(pool)) return RESOURCE_POOL_FILTER_TYPE.ROOT_ACCOUNT
    return normalizeResourcePoolType(pool.resource_pool_type)
}

export function resourcePoolTypeLabel(pool) {
    if (isRootResourcePool(pool)) return 'Root account'

    return (
        {
            [RESOURCE_POOL_TYPE.ACCOUNT]: 'Account',
            [RESOURCE_POOL_TYPE.WALLET]: 'Wallet'
        }[normalizeResourcePoolType(pool.resource_pool_type)] || 'Unknown type'
    )
}

export function resourcePoolIcon(pool) {
    if (isRootResourcePool(pool)) return 'mdi-briefcase-variant-outline'

    return (
        {
            [RESOURCE_POOL_TYPE.ACCOUNT]: 'mdi-account-box-outline',
            [RESOURCE_POOL_TYPE.WALLET]: 'mdi-wallet-outline'
        }[normalizeResourcePoolType(pool.resource_pool_type)] || 'mdi-help-circle-outline'
    )
}

export function resourcePoolHierarchyLabel(pool = {}, items = []) {
    const parent = items.find(candidate => isSameResourcePoolId(candidate.rpid, pool.parent_rpid))
    const root = items.find(candidate => isSameResourcePoolId(candidate.rpid, pool.root_rpid))

    if (pool.parent_long_id || parent?.long_id) return `Parent: ${pool.parent_long_id || parent.long_id}`
    if (!isSameResourcePoolId(pool.root_rpid, pool.rpid) && (pool.root_long_id || root?.long_id)) return `Root: ${pool.root_long_id || root.long_id}`
    return ''
}

export function resourcePoolMatchesSearch(pool, queryText, items = []) {
    const query = String(queryText || '')
        .trim()
        .toLowerCase()
    if (!query) return true

    const values = [
        pool.long_id,
        pool.name,
        resourcePoolTypeLabel(pool),
        resourcePoolHierarchyLabel(pool, items),
        pool.parent_long_id,
        pool.root_long_id,
        pool.descendant_count
    ]
    return values.filter(value => value !== null && value !== undefined).some(value => String(value).toLowerCase().includes(query))
}
